import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ShareFat } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import swal from 'sweetalert'
import AutocompleteClienteDefault from '../../components/autocomplete-cliente/autocomplete-clienteDefault'
import { CustomButton } from '../../components/CustomButton'
import { CustomPriceInput } from '../../components/CustomPriceInput'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import { adaptValue } from '../../utils/adaptValue'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'

// ─── Normaliza forma de pagamento ────────────────────────────────────────────
const normalizarFormaPgto = (forma) => {
  if (!forma) return ''
  const lower = forma.toLowerCase().trim()
  if (lower === 'boleto' || lower === 'boleto bancário') return 'Boleto'
  if (['cartao_debito', 'cartão de débito', 'cartão', 'debito'].includes(lower)) return 'cartao_debito'
  if (['cartao_credito', 'cartão de crédito', 'credito'].includes(lower)) return 'cartao_credito'
  if (['transferencia_bancaria', 'transferência bancária', 'transferencia'].includes(lower)) return 'transferencia_bancaria'
  if (lower === 'cheque') return 'cheque'
  if (lower === 'pix') return 'pix'
  if (lower === 'haver') return 'haver'
  if (lower === 'dinheiro') return 'dinheiro'
  return forma
}

// ─── Ícones inline (sem deps extras) ─────────────────────────────────────────
const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
)

const ICONS = {
  check:    'M20 6L9 17l-5-5',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
  print:    'M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z',
  note:     'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
}

// ─── Field ────────────────────────────────────────────────────────────────────
const Field = ({ label, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <label style={{
      display: 'block',
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--clr-muted)',
      marginBottom: '0.35rem',
    }}>
      {label}
    </label>
    {children}
  </div>
)

// ─── Estilos ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --clr-bg:        #f0f2f7;
    --clr-surface:   #ffffff;
    --clr-border:    #e2e6ee;
    --clr-accent:    #3b5bdb;
    --clr-accent-lt: #eef1fd;
    --clr-success:   #2fb67a;
    --clr-text:      #1a1d2e;
    --clr-muted:     #8890a4;
    --clr-danger:    #e74c3c;
    --radius-sm:     6px;
    --radius-md:     12px;
    --radius-lg:     20px;
    --shadow-card:   0 2px 24px rgba(59,91,219,.07), 0 1px 4px rgba(26,29,46,.06);
    --shadow-hover:  0 6px 32px rgba(59,91,219,.13);
    --font-main:     'DM Sans', sans-serif;
    --font-mono:     'DM Mono', monospace;
    --transition:    0.2s cubic-bezier(.4,0,.2,1);
  }

  .ctr-page {
    font-family: var(--font-main);
    background: var(--clr-bg);
    min-height: 100vh;
    padding: 2rem 1rem 4rem;
  }

  .ctr-wrapper {
    max-width: 860px;
    margin: 0 auto;
    animation: fadeUp .4s ease both;
  }

  @keyframes fadeUp {
    from { opacity:0; transform:translateY(16px) }
    to   { opacity:1; transform:translateY(0) }
  }

  /* ── Header ── */
  .ctr-header {
    background: var(--clr-surface);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-card);
    padding: 1.75rem 2rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    border-left: 4px solid var(--clr-accent);
  }
  .ctr-header-info h2 {
    margin: 0 0 .15rem;
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--clr-text);
    letter-spacing: -.02em;
  }
  .ctr-header-info p {
    margin: 0;
    font-family: var(--font-mono);
    font-size: .8rem;
    color: var(--clr-muted);
  }
  .ctr-badge {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    padding: .35rem .85rem;
    border-radius: 99px;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
  }
  .ctr-badge.pago { background:#e8faf2; color:var(--clr-success); }
  .ctr-badge.aberto { background:#fff8e7; color:#e08c2e; }
  .ctr-badge.vencida { background:#fdecea; color:var(--clr-danger); }
  .ctr-badge.default { background:var(--clr-accent-lt); color:var(--clr-accent); }

  /* ── Card ── */
  .ctr-card {
    background: var(--clr-surface);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-card);
    padding: 2rem;
    margin-bottom: 1rem;
  }
  .ctr-card-title {
    font-size: .65rem;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: var(--clr-accent);
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: .5rem;
  }
  .ctr-card-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--clr-border);
  }

  /* ── Grid ── */
  .ctr-grid {
    display: grid;
    gap: 1rem;
  }
  .ctr-grid-3 { grid-template-columns: repeat(3, 1fr); }
  .ctr-grid-2 { grid-template-columns: repeat(2, 1fr); }
  .ctr-grid-1 { grid-template-columns: 1fr; }

  /* ── Inputs ── */
  .ctr-input, .ctr-select, .ctr-textarea {
    width: 100%;
    background: #f7f9fc;
    border: 1.5px solid var(--clr-border);
    border-radius: var(--radius-sm);
    padding: .55rem .85rem;
    font-family: var(--font-main);
    font-size: .88rem;
    color: var(--clr-text);
    transition: border-color var(--transition), box-shadow var(--transition), background var(--transition);
    outline: none;
    box-sizing: border-box;
  }
  .ctr-input:focus, .ctr-select:focus, .ctr-textarea:focus {
    border-color: var(--clr-accent);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(59,91,219,.1);
  }
  .ctr-input:disabled {
    background: var(--clr-accent-lt);
    color: var(--clr-accent);
    font-family: var(--font-mono);
    font-size: .82rem;
    cursor: not-allowed;
    border-color: transparent;
  }
  .ctr-textarea { resize: vertical; min-height: 90px; line-height: 1.5; }
  .ctr-select { cursor: pointer; appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238890a4' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right .75rem center;
    padding-right: 2rem;
  }

  /* ── Autocomplete wrapper ── */
  .ctr-autocomplete-wrap { position: relative; }
  .ctr-autocomplete-wrap input {
    width: 100% !important;
    background: #f7f9fc !important;
    border: 1.5px solid var(--clr-border) !important;
    border-radius: var(--radius-sm) !important;
    padding: .55rem .85rem !important;
    font-family: var(--font-main) !important;
    font-size: .88rem !important;
    color: var(--clr-text) !important;
    transition: border-color var(--transition), box-shadow var(--transition) !important;
    outline: none !important;
    box-shadow: none !important;
  }
  .ctr-autocomplete-wrap input:focus {
    border-color: var(--clr-accent) !important;
    background: #fff !important;
    box-shadow: 0 0 0 3px rgba(59,91,219,.1) !important;
  }

  /* ── Botão adicionar cliente ── */
  .ctr-btn-ghost {
    display: inline-flex; align-items: center; gap: .4rem;
    padding: .4rem .9rem;
    border: 1.5px dashed var(--clr-accent);
    border-radius: var(--radius-sm);
    background: var(--clr-accent-lt);
    color: var(--clr-accent);
    font-family: var(--font-main);
    font-size: .8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition);
  }
  .ctr-btn-ghost:hover { background: var(--clr-accent); color:#fff; }

  /* ── Submit ── */
  .ctr-btn-submit {
    width: 100%;
    padding: .85rem 1.5rem;
    background: var(--clr-accent);
    color: #fff;
    border: none;
    border-radius: var(--radius-md);
    font-family: var(--font-main);
    font-size: .95rem;
    font-weight: 700;
    letter-spacing: .02em;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: .6rem;
    transition: all var(--transition);
    box-shadow: 0 4px 14px rgba(59,91,219,.3);
  }
  .ctr-btn-submit:hover { background: #2f4ac0; box-shadow: 0 6px 20px rgba(59,91,219,.4); transform: translateY(-1px); }
  .ctr-btn-submit:active { transform: translateY(0); }
  .ctr-btn-submit:disabled { opacity:.6; cursor:not-allowed; transform:none; }

  /* ── Ações inferiores ── */
  .ctr-actions {
    display: flex;
    gap: .75rem;
    flex-wrap: wrap;
  }
  .ctr-action-btn {
    flex: 1;
    min-width: 130px;
    display: inline-flex; align-items: center; justify-content: center; gap: .45rem;
    padding: .65rem 1rem;
    border: 1.5px solid var(--clr-border);
    border-radius: var(--radius-sm);
    background: var(--clr-surface);
    color: var(--clr-text);
    font-family: var(--font-main);
    font-size: .82rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition);
  }
  .ctr-action-btn:hover { border-color: var(--clr-accent); color: var(--clr-accent); background: var(--clr-accent-lt); }
  .ctr-action-btn.danger:hover { border-color: var(--clr-danger); color: var(--clr-danger); background: #fdecea; }

  /* ── Modal ── */
  .ctr-modal-body { display: flex; flex-direction: column; gap: 1rem; padding: 1.5rem !important; }

  /* ── Responsive ── */
  @media(max-width:680px) {
    .ctr-grid-3 { grid-template-columns: 1fr 1fr; }
    .ctr-header { flex-direction: column; align-items: flex-start; }
  }
  @media(max-width:420px) {
    .ctr-grid-3, .ctr-grid-2 { grid-template-columns: 1fr; }
  }
`

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getBadge = (situacao) => {
  const s = (situacao || '').toLowerCase()
  if (s === 'pago' || s === 'recebido') return { cls: 'pago', label: situacao }
  if (s === 'vencida') return { cls: 'vencida', label: 'Vencida' }
  if (s === 'em aberto') return { cls: 'aberto', label: 'Em Aberto' }
  return { cls: 'default', label: situacao || 'Sem status' }
}

// ─── Componente Principal ─────────────────────────────────────────────────────
function EditarContasReceber() {
  const [id, setId]                       = useState('')
  const { idConta }                       = useParams()
  const [idFornecedor, setIdFornecedor]   = useState('')
  const [formaDePgto, setFormaDePgto]     = useState('')
  const [numeroIdent, setNumeroIdent]     = useState('')
  const [numeroDoc, setNumeroDoc]         = useState('')
  const [vencimento, setVencimento]       = useState('')
  const [valor, setValor]                 = useState('')
  const [situacao, setSituacao]           = useState('')
  const [vencimentoOriginal, setVencimentoOriginal] = useState('')
  const [dataEmissao, setDataEmissao]     = useState('')
  const [competencia, setCompetencia]     = useState('')
  const [portador, setPortador]           = useState('')
  const [categoria, setCategoria]         = useState('')
  const [ocorrencia, setOcorrencia]       = useState('')
  const [historicoConta, setHistoricoConta] = useState('')
  const [dataPgto, setDataPgto]           = useState('')
  const [subCategoria, setSubCategoria]   = useState('')
  const [idNfeEntrada, setIdNfeEntrada]   = useState('')
  const [idPedidoCompra, setIdPedidoCompra] = useState('')
  const [pesRazao, setPesRazao]           = useState('')
  const [pesCpf, setPesCPf]              = useState('')
  const [pago, setPago]                   = useState(false)
  const conta                             = localStorage.idconta
  const navigate                          = useNavigate()
  const [categoriaOptions, setCategoriaOptions] = useState([])
  const [isModalOpen, setIsModalOpen]     = useState(false)
  const [razaoSocial, setRazaoSocial]     = useState('')
  const [cpfCnpj, setCpfCnpj]            = useState('')
  const [isSendAccountLoading, setIsSendAccountLoading] = useState(false)

  const badge = getBadge(situacao)

  function handleKeyDown(e) { if (e.keyCode === 13) e.preventDefault() }

  useEffect(() => {
    apinode.get(`contas/categoria/${localStorage.idconta}`)
      .then(r => setCategoriaOptions(r.data))
      .catch(e => console.error(e))
  }, [])

  const formatCpfCnpj = (value) => {
    if (!value) return ''
    const n = value.replace(/[^\d]/g, '')
    if (n.length <= 11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }

  const handleNomeCliente = (nome) => setPesRazao(nome)
  const handleIdCliente   = (id)   => setIdFornecedor(id)
  const handleCpf         = (cpf)  => setPesCPf(cpf)

  const handleOpenModal  = (e) => { e.preventDefault(); setIsModalOpen(true) }
  const handleCloseModal = ()  => setIsModalOpen(false)

  const SalvarCliente = () => {
    apinode.post(`/contasreceber/addcliente/${conta}`, { PES_RAZAO: razaoSocial, PES_CNPJ_CPF: cpfCnpj })
      .then(r  => swal('Sucesso', r.data.retorno, 'success'))
      .catch(e => swal('Erro', e.response.data.retorno, 'warning'))
  }

  const handleBaixarConta = () => {
    if (!window.confirm('Deseja baixar as contas selecionadas?')) return
    apinode.put(`/contasreceber/pgconta/${conta}?ids=${idConta}`, {})
      .then(r  => { swal('Sucesso', r.data.retorno, 'success'); ListarContas() })
      .catch(e => swal('Erro', e.response.data.retorno, 'warning'))
  }

  async function enviarConta() {
    const dados = {
      ID_FORNCEDOR: idFornecedor, FORMA_DE_PGTO: formaDePgto,
      NUMERO_IDENT: numeroIdent, NUMERO_DOC: numeroDoc,
      VENCIMENTO: vencimento, VALOR: adaptValue(valor),
      SITUACAO: situacao, VENCIMENTO_ORIGINAL: vencimentoOriginal,
      DATA_EMISSAO: dataEmissao, COMPETENCIA: competencia,
      PORTADOR: portador, CATEGORIA: categoria,
      OCORRENCIA: ocorrencia, HISTORICO_CONTA: historicoConta,
      SUB_CATEGORIA: subCategoria,
      IDNFEENTRADA: idNfeEntrada || null, IDPEDIDOCOMPRA: idPedidoCompra || null,
      DATA_PGTO: dataPgto || null,
    }
    try {
      setIsSendAccountLoading(true)
      if (idConta) {
        await apinode.put(`/contasreceber/updateconta/${localStorage.idconta}/${idConta}`, dados)
      } else {
        await apinode.post(`/contasreceber/addconta/${localStorage.idconta}`, dados)
      }
      toast.success('Conta salva com sucesso!')
      navigate('/contas-a-receber')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao enviar conta')
    } finally {
      setIsSendAccountLoading(false)
    }
  }

  function formatarData(data) {
    if (!data) return null
    const d = new Date(data)
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  }

  useEffect(() => { if (idConta) ListarContas() }, [idConta])

  function ListarContas() {
    apinode.get(`contasreceber/contareceber/${idConta}/${localStorage.idconta}`)
      .then(ret => {
        if (ret.status !== 200) return alert('Erro ao processar...')
        const d = ret.data[0]
        setId(d.ID); setIdFornecedor(d.ID_FORNCEDOR); setPesRazao(d.PES_RAZAO)
        setFormaDePgto(normalizarFormaPgto(d.FORMA_DE_PGTO))
        setNumeroIdent(d.NUMERO_IDENT); setNumeroDoc(d.NUMERO_DOC)
        setVencimento(formatarData(d.VENCIMENTO))
        setValor(d.VALOR.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
        setSituacao(d.SITUACAO); setVencimentoOriginal(formatarData(d.VENCIMENTO_ORIGINAL))
        setDataEmissao(formatarData(d.DATA_EMISSAO)); setCompetencia(formatarData(d.COMPETENCIA))
        setPortador(d.PORTADOR); setCategoria(d.CATEGORIA); setOcorrencia(d.OCORRENCIA)
        setHistoricoConta(d.HISTORICO_CONTA); setIdNfeEntrada(d.IDNFEENTRADA)
        setIdPedidoCompra(d.IDPEDIDOCOMPRA); setPesCPf(d.PES_CNPJ_CPF)
        setDataPgto(formatarData(d.DATA_PGTO))
        if (d.SITUACAO === 'Pago' || d.SITUACAO === 'Recebido') setPago(true)
      })
      .catch(e => console.error(e))
  }

  return (
    <>
      <style>{styles}</style>
      <Navbar />

      <div className="ctr-page">
        <div className="ctr-wrapper">

          {/* ── Header ── */}
          <div className="ctr-header">
            <div className="ctr-header-info">
              <h2>{pesRazao || (idConta ? 'Carregando…' : 'Nova Conta a Receber')}</h2>
              {pesCpf && <p>{formatCpfCnpj(pesCpf)}</p>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap' }}>
              {id && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.75rem', color: 'var(--clr-muted)', background: 'var(--clr-bg)', padding: '.3rem .7rem', borderRadius: '99px' }}>
                  # {id}
                </span>
              )}
              {situacao && (
                <span className={`ctr-badge ${badge.cls}`}>
                  {badge.cls === 'pago' && <Icon d={ICONS.check} size={12} color="currentColor" />}
                  {badge.label}
                </span>
              )}
            </div>
          </div>

          {/* ── Seção: Cliente ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Cliente</div>
            <div style={{ marginBottom: '0.75rem' }}>
              <Field label="Pesquisar cliente">
                <div className="ctr-autocomplete-wrap">
                  <AutocompleteClienteDefault
                    placeholder="Nome ou documento..."
                    onClickNome={handleNomeCliente}
                    onClickId={handleIdCliente}
                    onClickCpf={handleCpf}
                    value={pesRazao}
                    className="form-control"
                  />
                </div>
              </Field>
            </div>
            <button className="ctr-btn-ghost" onClick={handleOpenModal}>
              <FontAwesomeIcon icon={faPlusCircle} size="sm" />
              Cadastrar novo cliente
            </button>
          </div>

          {/* ── Seção: Identificação ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Identificação</div>
            <div className="ctr-grid ctr-grid-3">
              <Field label="ID">
                <input disabled className="ctr-input" value={id} onChange={e => setId(e.target.value)} />
              </Field>
              <Field label="Cód. Fornecedor">
                <input className="ctr-input" value={idFornecedor} onChange={e => setIdFornecedor(e.target.value)} />
              </Field>
              <Field label="Forma de Pagamento">
                <select className="ctr-select" value={normalizarFormaPgto(formaDePgto)} onChange={e => setFormaDePgto(e.target.value)}>
                  <option value="">Selecione...</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="cartao_debito">Cartão de Débito</option>
                  <option value="cartao_credito">Cartão de Crédito</option>
                  <option value="transferencia_bancaria">Transferência Bancária</option>
                  <option value="Boleto">Boleto Bancário</option>
                  <option value="cheque">Cheque</option>
                  <option value="pix">PIX</option>
                  <option value="haver">Haver</option>
                </select>
              </Field>
              <Field label="Nº Identificação">
                <input className="ctr-input" value={numeroIdent} onChange={e => setNumeroIdent(e.target.value)} onKeyDown={handleKeyDown} />
              </Field>
              <Field label="Nº Documento">
                <input className="ctr-input" value={numeroDoc} onChange={e => setNumeroDoc(e.target.value)} onKeyDown={handleKeyDown} />
              </Field>
              <Field label="Ocorrência">
                <input className="ctr-input" value={ocorrencia} onChange={e => setOcorrencia(e.target.value)} />
              </Field>
            </div>
          </div>

          {/* ── Seção: Valores e Situação ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Valores & Situação</div>
            <div className="ctr-grid ctr-grid-3">
              <Field label="Valor (R$)">
                <CustomPriceInput value={valor} onChange={e => setValor(e.target.value)} className="ctr-input" />
              </Field>
              <Field label="Situação">
                <select className="ctr-select" value={situacao} onChange={e => setSituacao(e.target.value)}>
                  <option value="-1">-- Não Selecionado --</option>
                  <option value="Em Aberto">Em Aberto</option>
                  <option value="Pago">Pago</option>
                  <option value="Aguardando Pagamento">Aguardando Pagamento</option>
                  <option value="Vencida">Vencida</option>
                  <option value="Recebido">Recebido</option>
                  <option value="Cartorio">Cartório</option>
                </select>
              </Field>
              <Field label="Pago em">
                <input type="date" className="ctr-input" value={dataPgto} onChange={e => setDataPgto(e.target.value)} />
              </Field>
            </div>
          </div>

          {/* ── Seção: Datas ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Datas</div>
            <div className="ctr-grid ctr-grid-3">
              <Field label="Vencimento">
                <input type="date" className="ctr-input" value={vencimento} onChange={e => setVencimento(e.target.value)} />
              </Field>
              <Field label="Venc. Original">
                <input type="date" className="ctr-input" value={vencimentoOriginal} onChange={e => setVencimentoOriginal(e.target.value)} />
              </Field>
              <Field label="Data de Emissão">
                <input type="date" className="ctr-input" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} />
              </Field>
              <Field label="Competência">
                <input type="date" className="ctr-input" value={competencia} onChange={e => setCompetencia(e.target.value)} />
              </Field>
            </div>
          </div>

          {/* ── Seção: Classificação ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Classificação</div>
            <div className="ctr-grid ctr-grid-3">
              <Field label="Categoria">
                <select className="ctr-select" value={categoria} onChange={e => setCategoria(e.target.value)}>
                  <option value="">Selecione...</option>
                  {categoriaOptions.map((opt, i) => (
                    <option key={i} value={opt.CODIGO}>{opt.DESCRICAO}</option>
                  ))}
                </select>
              </Field>
              <Field label="Sub Categoria">
                <input className="ctr-input" value={subCategoria} onChange={e => setSubCategoria(e.target.value)} />
              </Field>
              <Field label="Portador">
                <input className="ctr-input" value={portador} onChange={e => setPortador(e.target.value)} />
              </Field>
              <Field label="NFe Entrada">
                <input className="ctr-input" value={idNfeEntrada} onChange={e => setIdNfeEntrada(e.target.value)} />
              </Field>
              <Field label="Pedido de Compra">
                <input className="ctr-input" value={idPedidoCompra} onChange={e => setIdPedidoCompra(e.target.value)} />
              </Field>
            </div>
          </div>

          {/* ── Seção: Observações ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Observações</div>
            <Field label="Histórico / Obs">
              <textarea className="ctr-textarea" rows={4} value={historicoConta} onChange={e => setHistoricoConta(e.target.value)} placeholder="Adicione observações relevantes sobre esta conta..." />
            </Field>
          </div>

          {/* ── Salvar ── */}
          <div className="ctr-card" style={{ padding: '1.5rem' }}>
            <button
              className="ctr-btn-submit"
              onClick={enviarConta}
              disabled={isSendAccountLoading}
            >
              {isSendAccountLoading
                ? <><span style={{ width:16, height:16, border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin .7s linear infinite' }} /> Enviando...</>
                : <><ShareFat size={18} weight="fill" /> Salvar conta</>
              }
            </button>
          </div>

          {/* ── Ações ── */}
          <div className="ctr-card">
            <div className="ctr-card-title">Ações</div>
            <div className="ctr-actions">
              <button className="ctr-action-btn danger" onClick={handleBaixarConta}>
                <Icon d={ICONS.download} size={15} color="currentColor" />
                Baixar Conta
              </button>
              <button className="ctr-action-btn">
                <Icon d={ICONS.print} size={15} color="currentColor" />
                Imprimir Recibo
              </button>
              <button className="ctr-action-btn">
                <Icon d={ICONS.settings} size={15} color="currentColor" />
                Alterar Andamento
              </button>
              <button className="ctr-action-btn">
                <Icon d={ICONS.note} size={15} color="currentColor" />
                Add Observações
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* ── Modal: Cadastrar Cliente ── */}
      <Modal show={isModalOpen} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid var(--clr-border)', padding: '1.25rem 1.5rem' }}>
          <Modal.Title style={{ fontFamily: 'var(--font-main)', fontWeight: 700, fontSize: '1rem' }}>
            Cadastrar Cliente
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ctr-modal-body">
          <Field label="CPF / CNPJ">
            <input type="text" className="ctr-input" value={formatCpfCnpj(cpfCnpj)} onChange={e => setCpfCnpj(e.target.value)} placeholder="000.000.000-00" />
          </Field>
          <Field label="Razão Social">
            <input type="text" className="ctr-input" value={razaoSocial} onChange={e => setRazaoSocial(e.target.value)} placeholder="Nome ou razão social" />
          </Field>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid var(--clr-border)', padding: '1rem 1.5rem', gap: '.6rem' }}>
          <button className="ctr-action-btn" style={{ flex: 0, minWidth: 100 }} onClick={handleCloseModal}>Fechar</button>
          <button className="ctr-btn-submit" style={{ flex: 0, minWidth: 120, padding: '.6rem 1.2rem', fontSize: '.85rem' }} onClick={SalvarCliente}>Salvar</button>
        </Modal.Footer>
      </Modal>

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </>
  )
}

export default EditarContasReceber